<template>
	<form v-if="lite" class="jobs-search-form" :class="{ 'is-home': is_home }" :action="search_url">
		<input
			v-for="field in default_fields"
			:key="`${field.name}:${field.value}`"
			type="hidden"
			:name="field.name"
			:value="field.value"
		/>

		<div class="row row-inputs">
			<suggest
				v-model="data.query"
				:api_url="api_url"
				slug="query"
				label="Search"
				placeholder="Job title, keywords, or company"
			/>

			<suggest
				v-model="data.location"
				:api_url="api_url"
				slug="location"
				label="Location"
				placeholder="City, state"
			/>

			<button type="submit" class="btn btn-solid">
				<v-icon slug="magnifying-glass" />
				<span>{{ button_label }}</span>
			</button>
		</div>
	</form>

	<div v-else class="app">
		<div class="wrap">
			<form class="jobs-search-form" @submit.prevent="submit">
				<div class="row row-inputs">
					<suggest
						v-model="data.query"
						slug="query"
						label="Search"
						:api_url="store.api_url"
						placeholder="Job title, keywords, or company"
					/>

					<suggest
						v-model="data.location"
						slug="location"
						label="Location"
						:api_url="store.api_url"
						placeholder="City, state"
					/>

					<button type="submit" class="btn btn-solid">
						<v-icon slug="magnifying-glass" />
						<span>Search</span>
					</button>
				</div>

				<div class="row-filters-container">
					<button type="button" class="btn btn-bordered btn-mobile" @click="show_all_filters_modal = true">
						<v-icon slug="bars-filter" />
						<span class="screen-reader-text">All Filters</span>

						<span v-if="active_filters_count > 0" class="filters-count">
							{{ active_filters_count }}
						</span>
					</button>

					<div class="row row-filters">
						<filter-checkboxes
							v-model="data.work_environments"
							label="Remote options"
							mobile_label="Remote"
							:options="store.lists.work_environments"
							@change="submit"
						/>

						<filter-salary
							v-model:salary_period="data.salary_period"
							v-model:require_salary="data.require_salary"
							v-model:salary_min="data.salary_min"
							label="Salary"
							mobile_label="Pay"
							@change="submit"
						/>

						<filter-checkboxes
							v-model="data.work_schedules"
							label="Work schedule"
							mobile_label="Schedule"
							:options="store.lists.work_schedules"
							@change="submit"
						/>

						<filter-checkboxes
							v-model="data.employment_types"
							label="Employment type"
							mobile_label="Type"
							:options="store.lists.employment_types"
							@change="submit"
						/>

						<filter-checkboxes
							v-model="data.experiences"
							label="Experience"
							:options="store.lists.experiences"
							@change="submit"
						/>

						<filter-checkboxes
							v-model="data.qualifications"
							label="Education"
							:options="store.lists.qualifications"
							@change="submit"
						/>

						<button type="button" class="btn btn-bordered" @click="show_all_filters_modal = true">
							<v-icon slug="bars-filter" />
							<span>All Filters</span>

							<span v-if="active_filters_count > 0" class="filters-count">
								{{ active_filters_count }}
							</span>
						</button>
					</div>
				</div>
			</form>

			<all-filters-modal
				v-if="show_all_filters_modal"
				:data="data"
				:defaults="store.defaults"
				:filter_keys="resettable_keys"
				@close="show_all_filters_modal = false"
				@apply="apply_all_filters"
			/>

			<spinner v-if="loading && items.length === 0" />

			<template v-else>
				<h1>
					<template v-if="store.title && is_default">
						{{ store.title }}
					</template>

					<template v-else> Job search results </template>
				</h1>

				<div
					v-if="limited_total > 0 && items.length > 0"
					class="jobs-total"
					aria-live="polite"
					aria-atomic="true"
				>
					<p>{{ total_label }} {{ headline_count === 1 ? 'job' : 'jobs' }}{{ filters_summary }}</p>

					<div class="bubble green small">
						<v-icon slug="check" />
						<span>Verified hourly</span>
					</div>
				</div>

				<results-filters
					v-model:sort="data.sort"
					v-model:date="data.date"
					v-model:work_environments="data.work_environments"
					v-model:salary_period="data.salary_period"
					v-model:require_salary="data.require_salary"
					v-model:salary_min="data.salary_min"
					v-model:work_schedules="data.work_schedules"
					v-model:employment_types="data.employment_types"
					v-model:experiences="data.experiences"
					v-model:qualifications="data.qualifications"
					v-model:radius="data.radius"
					:location="submitted_location"
					:resettable="filters_are_resettable"
					@change="submit"
					@reset="reset"
				/>

				<div
					v-if="!submitted_location && geo_origin_city_state && !geo_origin_dismissed"
					class="jobs-geo-origin"
				>
					<button type="button" class="btn bubble orange small" @click="apply_geo_origin">
						<v-icon slug="location-dot" />

						<span
							>Show jobs near <strong>{{ geo_origin_city_state }}</strong
							>?</span
						>
					</button>

					<button
						type="button"
						class="btn geo-origin-dismiss"
						aria-label="Dismiss"
						@click="geo_origin_dismissed = true"
					>
						<v-icon slug="xmark" />
					</button>
				</div>

				<div v-if="store.content" class="jobs-description" v-html="store.content" />

				<div v-if="items.length > 0" class="jobs-results">
					<template v-for="item in items" :key="item.uuid">
						<job-deck
							v-if="item.variant_preview && item.variant_preview.length > 1"
							:item="item"
							:load_page="
								(loaded, signal) =>
									load_stack_page({
										api_url: store.api_url,
										base_body: build_search_body(),
										item,
										loaded,
										signal,
									})
							"
						/>

						<job v-else :item="item" />
					</template>
				</div>

				<div v-else class="jobs-no-results">
					<p v-if="filters_are_resettable">
						Sorry, we couldn’t find any relevant results. First, try
						<button type="button" class="btn btn-link" @click="reset">clearing your filters</button>. Then:
					</p>

					<p v-else>Sorry, we couldn’t find any relevant results. Try:</p>

					<ul>
						<li>Try other (or less specific) keywords</li>
						<li>Check for spelling or other errors</li>
						<li>Replace abbreviations with the full word (or words)</li>
					</ul>
				</div>

				<footer v-if="data.page * data.limit < limited_total" class="jobs-load-more">
					<a v-if="next_url" :href="next_url" class="btn btn-bordered" @click.prevent="load_more">
						Load More Jobs
					</a>
				</footer>
			</template>
		</div>
	</div>
</template>

<style lang="scss" scoped>
@use 'sass:math';

@use '/sass/variables' as *;
@use '/sass/toolkit' as *;

.app {
	> .wrap {
		@include wrap;
	}
}

@include media('<=landscape') {
	// Keep containment off `.app` so overlays (e.g. the suggest dropdown)
	// aren't clipped/trapped; isolate the reflow-heavy results list instead.
	.jobs-results {
		contain: content;
	}
}

.jobs-search-form {
	background-color: $white;
	padding: $spacer;
	border-radius: $spacer;
	margin-bottom: $spacer-block;

	&.is-home {
		margin-bottom: $spacer;
		padding: 1em;
		border: 2px solid rgba(255, 132, 51, 0.2);
		border-radius: 9999px;
		box-shadow: 0px 16px 40px rgba(0, 0, 0, 0.08);
	}

	.row {
		margin-bottom: 1em;
		padding-bottom: 1em;
		border-bottom: 1px solid $grey-border;
		display: flex;
		gap: 1em;
		align-items: center;

		&:last-child {
			margin-bottom: 0;
			padding-bottom: 0;
			border-bottom: none;
		}
	}

	.field {
		flex: 1 1 0;
	}

	.btn-solid {
		flex: 0 0 auto;

		svg {
			display: none;
		}
	}

	.btn-bordered {
		font-size: 1em;
		margin-left: auto;

		span {
			flex: 0 0 auto;
		}

		.filters-count {
			display: flex;
			flex: 0 0 auto;
			align-items: center;
			justify-content: center;
			min-width: 1.5em;
			height: 1.5em;
			padding: 0 0.375em;
			border-radius: 9999px;
			background-color: $orange;
			color: $white;
			font-size: 0.75rem;
		}
	}
}

.jobs-search-form {
	.row-filters {
		flex: 1 1 0;
	}
}

@include media('<=landscape') {
	.jobs-search-form {
		.row-filters {
			overflow-x: scroll;
			scrollbar-width: none;
			-ms-overflow-style: none;

			&::-webkit-scrollbar {
				display: none;
			}
		}
	}
}

@include media('<=phone') {
	.jobs-search-form {
		padding: math.div($spacer, 2);
		margin-bottom: 1em;

		&.is-home {
			padding: 1em;
			border-radius: 1em;
		}

		> .row {
			margin-bottom: 0.5em;
			padding-bottom: 0;
			border-bottom: none;
			display: grid;
			grid-template-columns: 1fr 3em;

			.field {
				grid-column: auto / span 2;

				&:first-child {
					grid-column: auto;
				}
			}

			.btn-solid {
				grid-column: 2;
				grid-row: 1;
				width: 3em;
				height: 3em;
				padding: 0;

				span {
					@include visuallyhidden;
				}

				svg {
					display: block;
				}
			}
		}

		.row {
			gap: 0.5em;
		}
	}
}

.row-filters-container {
	display: flex;
	align-items: center;
	gap: 1em;

	.btn-mobile {
		flex: 0 0 auto;
		width: $control-height;
		height: $control-height;
		padding: 0;
		position: relative;

		.filters-count {
			position: absolute;
			top: 0;
			right: 0;
			transform: translate(25%, -25%);
		}
	}
}

@include media('>landscape') {
	.row-filters-container {
		.btn-mobile {
			display: none;
		}
	}
}

@include media('<=landscape') {
	.row-filters-container {
		display: flex;

		> div {
			.btn-bordered {
				display: none;
			}
		}
	}
}

@include media('<=phone') {
	.row-filters-container {
		gap: 0.5em;
	}
}

h1 {
	@include h3;
	margin-bottom: $spacer;

	&:last-child {
		margin-bottom: 0;
	}
}

@include media('<=phone') {
	h1 {
		margin-bottom: math.div($spacer, 2);

		&:last-child {
			margin-bottom: 0;
		}
	}
}

.jobs-total {
	margin-bottom: math.div($spacer, 2);
	display: flex;
	align-items: center;
	gap: 0.5em;
	flex-wrap: wrap;

	p {
		color: $black;
		font-weight: bold;
		margin-bottom: 0;
	}
}

.jobs-geo-origin {
	display: flex;
	align-items: center;
	gap: 0.5em;
	margin-bottom: $spacer;

	&:last-child {
		margin-bottom: 0;
	}

	.bubble {
		cursor: pointer;
		background-color: $white;
		font-weight: normal;
	}

	.geo-origin-dismiss {
		svg {
			fill: $grey-meta;
		}
	}
}

.jobs-description {
	@include entry-content;
	padding: 1em $spacer;
	background-color: $white;
	border-radius: $spacer;
	margin-bottom: $spacer;

	&:last-child {
		margin-bottom: 0;
	}
}

.jobs-results {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: $spacer;
	margin-bottom: $spacer-block;

	&:last-child {
		margin-bottom: 0;
	}
}

@include media('<=landscape') {
	.jobs-results {
		grid-template-columns: repeat(2, 1fr);
	}
}

@include media('<=phone') {
	.jobs-results {
		grid-template-columns: 1fr;
	}
}

.jobs-load-more {
	display: flex;
	justify-content: center;
}
</style>

<style lang="scss">
@use 'sass:math';

@use '/sass/variables' as *;
@use '/sass/toolkit' as *;

.jobs-no-results {
	@include entry-content;
}
</style>

<script setup>
import { useUrlSearchParams } from '@vueuse/core';
import { notify } from 'notify';
import { computed, onMounted, reactive, ref } from 'vue';

import { fetch_app_data } from '../_partials/_app.js';
import { sync_query_params } from '../_partials/_utilities.js';
import AllFiltersModal from './_all-filters-modal.vue';
import FilterCheckboxes from './_filter-checkboxes.vue';
import FilterSalary from './_filter-salary.vue';
import { fetch_geo_origin, use_geo_origin } from './_geo-origin.js';
import Job from './_job.vue';
import JobDeck from './_job-deck.vue';
import ResultsFilters from './_results-filters.vue';
import Spinner from './_spinner.vue';
import { load_stack_page } from './_stack.js';
import { store } from './_store.js';
import Suggest from './_suggest.vue';
import VIcon from './_v-icon.vue';

const props = defineProps({
	api_url: {
		type: String,
		default: ``,
	},
	search_url: {
		type: String,
		default: ``,
	},
	is_home: {
		type: Boolean,
	},
	lite: {
		type: Boolean,
	},
	button_label: {
		type: String,
		default: `Find Jobs`,
	},
	defaults: {
		type: Object,
		default: () => ({}),
	},
});

const default_fields = computed(() => {
	const fields = [];

	for (const key in props.defaults) {
		if (!props.defaults.hasOwnProperty(key)) {
			continue;
		}

		const value = props.defaults[key];
		const values = Array.isArray(value) ? value : [value];

		for (const item of values) {
			fields.push({
				name: key,
				value: item,
			});
		}
	}

	return fields;
});

const params = useUrlSearchParams(`history`, {
	writeMode: `push`,
});

const data = reactive({
	query: ``,
	location: ``,
	work_environments: [],
	salary_period: ``,
	require_salary: `no`,
	salary_min: null,
	work_schedules: [],
	employment_types: [],
	experiences: [],
	qualifications: [],
	date: ``,
	sort: ``,
	radius: null,

	page: 1,
	limit: 12,
	search_mode: `hybrid`,
});

const submitted_location = ref(data.location);

const loading = ref(true);

const start = ref(1);

const is_default = ref(true);

const items = ref([]);

const total = ref(0);

const limited_total = computed(() => Math.min(total.value, 1000));

const headline_count = ref(0);

const headline_is_lower_bound = ref(false);

const total_label = computed(() => headline_count.value.toLocaleString() + (headline_is_lower_bound.value ? `+` : ``));

const filters_summary = ref(``);

const controller = ref(null);

const geo_origin = use_geo_origin();

const geo_origin_dismissed = ref(false);

const geo_origin_city_state = computed(() => {
	if (!geo_origin.value?.city || !geo_origin.value?.state) {
		return ``;
	}

	return `${geo_origin.value.city}, ${geo_origin.value.state}`;
});

const next_url = computed(() => {
	const url = new URL(window.location);

	url.searchParams.set(`pg`, data.page + 1);

	return url;
});

const breadcrumbs_set = ref(false);

const show_all_filters_modal = ref(false);

const array_keys = [`work_environments`, `work_schedules`, `employment_types`, `experiences`, `qualifications`];

const filter_groups = [
	{
		keys: [`work_environments`],
	},
	{
		keys: [`salary_period`, `require_salary`, `salary_min`],
		is_active: () => data.require_salary === `yes` || data.salary_min !== null,
	},
	{
		keys: [`work_schedules`],
	},
	{
		keys: [`employment_types`],
	},
	{
		keys: [`experiences`],
	},
	{
		keys: [`qualifications`],
	},
	{
		keys: [`date`],
		countable: false,
	},
];

const resettable_keys = filter_groups.flatMap((group) => group.keys);

const countable_filter_groups = filter_groups.filter((group) => group.countable !== false);

function apply_all_filters(changes) {
	Object.assign(data, changes);

	submit();
}

function values_are_equal(a, b) {
	if (Array.isArray(a) || Array.isArray(b)) {
		const array_a = Array.isArray(a) ? a : [];
		const array_b = Array.isArray(b) ? b : [];

		if (array_a.length !== array_b.length) {
			return false;
		}

		return array_a.every((item, index) => item === array_b[index]);
	}

	return a === b;
}

function group_is_active(group) {
	if (group.is_active) {
		return group.is_active();
	}

	return group.keys.some((key) => !values_are_equal(data[key], store.defaults[key]));
}

const active_filters_count = computed(() => countable_filter_groups.filter(group_is_active).length);

const filters_are_resettable = computed(() => filter_groups.filter(group_is_active).length > 0);

function set_filters_summary() {
	if (data.query) {
		filters_summary.value = ` for "${data.query}"`;
	} else {
		filters_summary.value = ``;
	}
}

function build_search_body() {
	const body = {
		query: data.query,
		location: data.location,
		work_environments: data.work_environments,
		require_salary: data.require_salary,
		work_schedules: data.work_schedules,
		employment_types: data.employment_types,
		experiences: data.experiences,
		qualifications: data.qualifications,
		date: data.date,
		page: data.page,
		limit: data.limit,
		search_mode: data.search_mode,
	};

	if (data.sort !== store.defaults.sort) {
		body.sort = data.sort;
	}

	if (data.location && data.radius !== store.defaults.radius) {
		body.radius = data.radius;
	}

	if (data.salary_min !== null) {
		body.salary_min = data.salary_min;
		body.salary_period = data.salary_period;
	}

	if (store.grouped) {
		body.grouped = true;
	}

	return body;
}

function load() {
	set_filters_summary();

	if (controller.value) {
		controller.value.abort();
	}

	controller.value = new AbortController();

	if (!data.location) {
		fetch_geo_origin(store.api_url);
	}

	is_default.value = true;
	for (const key in store.defaults) {
		if (!store.defaults.hasOwnProperty(key)) {
			continue;
		}

		if (values_are_equal(store.defaults[key], data[key])) {
			continue;
		}

		is_default.value = false;
		break;
	}

	if (!is_default.value && !breadcrumbs_set.value) {
		const breadcrumb_el = document.getElementById(`breadcrumbs`);
		if (breadcrumb_el) {
			breadcrumbs_set.value = true;

			breadcrumb_el.classList.add(`truncate`);

			const last_breadcrumb_el = document.querySelector(`#breadcrumbs .breadcrumb_last`);
			if (last_breadcrumb_el) {
				last_breadcrumb_el.innerHTML = `Search Results`;
			}
		}
	}

	loading.value = true;

	fetch_app_data(store.api_url, `/api/listings/search`, {
		method: `POST`,
		body: build_search_body(),
		signal: controller.value.signal,
	})
		.then((response) => {
			if (response.items) {
				for (const item of response.items) {
					items.value.push(item);
				}
			}

			loading.value = false;
			total.value = response.total;

			const exact = store.grouped ? response.total_listings_exact : response.total_exact;
			const capped = store.grouped ? response.total_listings : response.total;
			const is_lower_bound = store.grouped
				? response.total_listings_is_lower_bound
				: response.total_is_lower_bound;

			headline_count.value = exact ?? capped ?? response.estimated_total ?? 0;
			headline_is_lower_bound.value = (typeof exact === `undefined` || exact === null) && Boolean(is_lower_bound);
		})
		.catch((error) => {
			if (error.name === `AbortError`) {
				return;
			}

			loading.value = false;
			notify(`error`, error);
		});
}

function submit(pg) {
	submitted_location.value = data.location;

	const query = {};
	for (const key in store.defaults) {
		if (!store.defaults.hasOwnProperty(key)) {
			continue;
		}

		if (values_are_equal(data[key], store.defaults[key])) {
			continue;
		}

		query[key] = data[key];
	}

	if (pg > 1) {
		query.pg = pg;
	}

	sync_query_params(params, query);

	data.page = typeof pg === `number` ? pg : 1;

	items.value = [];

	total.value = 0;

	load();
}

function apply_geo_origin() {
	if (!geo_origin_city_state.value) {
		return;
	}

	data.location = geo_origin_city_state.value;

	submit();
}

function load_more() {
	data.page++;

	const query = {
		...params,
	};

	query.pg = data.page;

	sync_query_params(params, query);

	load();
}

function reset() {
	for (const key in store.defaults) {
		if (!store.defaults.hasOwnProperty(key)) {
			continue;
		}

		if ([`query`, `location`].indexOf(key) > -1) {
			continue;
		}

		data[key] = array_keys.indexOf(key) > -1 ? [...store.defaults[key]] : store.defaults[key];
	}

	submit();
}

onMounted(() => {
	if (props.lite) {
		return;
	}

	for (const key in store.defaults) {
		if (!store.defaults.hasOwnProperty(key)) {
			continue;
		}

		if (params.hasOwnProperty(key)) {
			if (array_keys.indexOf(key) > -1) {
				if (Array.isArray(params[key])) {
					data[key] = params[key];
				} else {
					data[key] = [params[key]];
				}
			} else if (key === `salary_min`) {
				const raw_value = Array.isArray(params[key]) ? params[key][0] : params[key];

				data[key] = raw_value ? Number(raw_value) : null;
			} else if (key === `radius`) {
				const raw_value = Array.isArray(params[key]) ? params[key][0] : params[key];

				data[key] = raw_value ? Number(raw_value) : store.defaults.radius;
			} else {
				data[key] = params[key];
			}

			continue;
		}

		const value = store.defaults[key];
		if (value === ``) {
			continue;
		}

		data[key] = value;
	}

	if (data.salary_min !== null && !data.salary_period) {
		data.salary_period = `yr`;
	}

	if (params.pg) {
		data.page = parseInt(params.pg, 10);
		start.value = (data.page - 1) * data.limit + 1;
		submit(data.page);
	} else {
		submit();
	}
});
</script>
